import { Modal as AntModal, Form, message, notification } from "antd";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { InvoiceSerie } from "../../models/entities/company";
import api from "../../services/api";

import ChannelRow from "./components/ChannelRow";
import ConfirmContent from "./components/ConfirmContent";
import FiscalWarning from "./components/FiscalWarning";
import SeriesHeader from "./components/SeriesHeader";
import { useDefaultSerie } from "./hooks/useDefaultSerie";
import {
  CancelButton,
  ConfirmIcon,
  Modal,
  SaveButton,
  StepForm,
  Form as StyledForm,
} from "./styles";
import {
  ChannelValue,
  NfceSeriesChannelConfig,
  saleChannels,
} from "./types";

interface ModalEdicaoSerieNFCeProps {
  visible: boolean;
  invoiceSeries?: InvoiceSerie[];
  companyId?: number;
  setShouldSearch: Dispatch<SetStateAction<boolean>>;
  setVisible: Dispatch<SetStateAction<boolean>>;
}

const InvoiceSerieForm: React.FC<ModalEdicaoSerieNFCeProps> = ({
  visible,
  invoiceSeries,
  companyId,
  setShouldSearch,
  setVisible,
}: ModalEdicaoSerieNFCeProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const { defaultSerie, loading: loadingDefault } = useDefaultSerie(
    visible,
    companyId
  );

  const initialValues = useMemo(
    () =>
      saleChannels.reduce(
        (acc, channel) => ({
          ...acc,
          [channel.id]: { serie: undefined, invoiceSequence: undefined },
        }),
        {} as Record<string, ChannelValue>
      ),
    []
  );
  const [values, setValues] =
    useState<Record<string, ChannelValue>>(initialValues);

  const savedByChannel = useMemo(() => {
    const map = new Map<number, InvoiceSerie>();
    (invoiceSeries ?? []).forEach((item) => map.set(item.type_sale, item));
    return map;
  }, [invoiceSeries]);

  const resolveCurrent = useCallback(
    (channelId: number) => {
      const saved = savedByChannel.get(channelId);
      if (saved?.serie != null) {
        return {
          serie: Number(saved.serie),
          invoiceSequence: Number(saved.invoice_sequence),
        };
      }
      if (defaultSerie?.serie != null) {
        return {
          serie: defaultSerie.serie,
          invoiceSequence: defaultSerie.invoiceSequence,
        };
      }
      return undefined;
    },
    [savedByChannel, defaultSerie]
  );

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setValues(initialValues);
    }
  }, [visible, form, initialValues]);

  const getChannelDiff = (
    channelId: number,
    source: Record<string, ChannelValue>
  ) => {
    const current = resolveCurrent(channelId);
    const value = source[channelId];
    const isDefault =
      value?.serie != null && value.serie === defaultSerie?.serie;

    const changed =
      (value?.serie != null && value.serie !== current?.serie) ||
      (value?.invoiceSequence != null &&
        value.invoiceSequence !== current?.invoiceSequence);
    return { current, value, isDefault, changed };
  };

  const hasAnyChange = saleChannels.some(
    (channel) => getChannelDiff(channel.id, values).changed
  );

  function handleValuesChange(
    changedValues: Record<string, ChannelValue>,
    allValues: unknown
  ) {
    const defaultSerieValue = defaultSerie?.serie;
    const next: Record<string, ChannelValue> = {
      ...(allValues as Record<string, ChannelValue>),
    };

    const changedId = Number(Object.keys(changedValues)[0]);
    const change = changedValues[changedId];
    const serie = next[changedId]?.serie;

    if (change && "serie" in change) {
      if (serie == null || serie === defaultSerieValue) {
        next[changedId] = { ...next[changedId], invoiceSequence: undefined };
      } else {
        const groupSequence = saleChannels
          .filter((c) => c.id !== changedId && next[c.id]?.serie === serie)
          .map((c) => next[c.id]?.invoiceSequence)
          .find((sequence) => sequence != null);
        if (groupSequence != null) {
          next[changedId] = {
            ...next[changedId],
            invoiceSequence: groupSequence,
          };
        }
      }
    } else if (change && "invoiceSequence" in change && serie != null) {
      const sequence = next[changedId]?.invoiceSequence;
      saleChannels.forEach((c) => {
        if (c.id !== changedId && next[c.id]?.serie === serie) {
          next[c.id] = { ...next[c.id], invoiceSequence: sequence };
        }
      });
    }

    form.setFieldsValue(next);
    setValues(next);
  }

  function getChangedChannels(submitted: Record<string, ChannelValue>) {
    return saleChannels.filter(
      (channel) => getChannelDiff(channel.id, submitted).changed
    );
  }

  function buildPayload(
    submitted: Record<string, ChannelValue>
  ): NfceSeriesChannelConfig[] {
    return saleChannels.map((channel) => {
      const saved = savedByChannel.get(channel.id);
      const value = submitted[channel.id];
      const editedSerie = value?.serie;

      if (editedSerie == null) {
        if (saved?.serie != null) {
          return {
            type_sale: channel.id,
            serie: Number(saved.serie),
            invoice_sequence: saved.invoice_sequence ?? null,
          };
        }
        return { type_sale: channel.id, serie: null, invoice_sequence: null };
      }

      if (defaultSerie?.serie != null && editedSerie === defaultSerie.serie) {
        return { type_sale: channel.id, serie: null, invoice_sequence: null };
      }

      return {
        type_sale: channel.id,
        serie: editedSerie,
        invoice_sequence: value?.invoiceSequence ?? null,
      };
    });
  }

  async function handleConfirm() {
    let submitted: Record<string, ChannelValue>;
    try {
      submitted = await form.validateFields();
    } catch (errorInfo) {
      const firstError = (
        errorInfo as { errorFields?: { name: (string | number)[] }[] }
      )?.errorFields?.[0]?.name;
      if (firstError) {
        form.scrollToField(firstError, { behavior: "smooth", block: "center" });
        document.getElementById(firstError.join("_"))?.focus();
      }
      return;
    }

    const changed = getChangedChannels(submitted);

    if (changed.length === 0) {
      setShouldSearch(true);
      setVisible(false);
      return;
    }

    AntModal.confirm({
      title: "Confirmar série e próximo número",
      icon: <ConfirmIcon />,
      centered: true,
      width: 520,
      okText: "Confirmar",
      cancelText: "Revisar",
      okType: "danger",
      okButtonProps: {
        loading,
        style: { width: "auto", minWidth: "7.5rem", padding: "0 1rem" },
      },
      content: (
        <ConfirmContent
          items={changed.map((channel) => {
            const { current, value } = getChannelDiff(channel.id, submitted);
            return { channel, current, value };
          })}
        />
      ),
      async onOk() {
        try {
          setLoading(true);
          const payload = buildPayload(submitted);
          await api.patch(`/company/${companyId}`, {
            nfce_series_by_sales_channel: payload,
          });

          setShouldSearch(true);
          setVisible(false);
          message.success("Série/Próximo número alterado com sucesso!");
        } catch (e) {
          //@ts-ignore
          const _description = e?.response?.data?.message;
          notification.error({
            message: `Erro ao alterar série/próximo número de tipos de venda`,
            description: _description,
            duration: 3,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  }

  return (
    <Modal
      visible={visible}
      title="Edição do número de série de NFC-e"
      onCancel={() => setVisible(false)}
      confirmLoading={loading}
      maskClosable={false}
      width={720}
      footer={[
        <CancelButton
          key="cancel"
          type="text"
          onClick={() => setVisible(false)}
        >
          Cancelar
        </CancelButton>,
        <SaveButton
          key="save"
          loading={loading}
          disabled={!hasAnyChange}
          onClick={handleConfirm}
        >
          Confirmar
        </SaveButton>,
      ]}
    >
      <FiscalWarning />

      <StyledForm
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <SeriesHeader defaultSerie={defaultSerie} loading={loadingDefault} />

        <StepForm>
          {saleChannels.map((channel) => {
            const current = resolveCurrent(channel.id);
            const { isDefault, changed } = getChannelDiff(channel.id, values);
            const sequenceDisabled =
              values[channel.id]?.serie == null || isDefault;
            return (
              <ChannelRow
                key={channel.id}
                channel={channel}
                current={current}
                changed={changed}
                sequenceDisabled={sequenceDisabled}
                defaultSerieValue={defaultSerie?.serie}
                loading={loadingDefault}
              />
            );
          })}
        </StepForm>
      </StyledForm>
    </Modal>
  );
};

export default InvoiceSerieForm;
